

import React, { createContext, useState, useEffect, useCallback } from 'react';

export const ShopContext = createContext();

const ShopContextProvider = (props) => {
  const currency = '₹';
  const delivery_fee = 0;
  
  // Backend URL for products
  const backendURL = 'https://sc-backend-9nhi.onrender.com';

  // State for cart
  const loadCartFromSessionStorage = () => {
    const savedCart = JSON.parse(sessionStorage.getItem('cart'));
    return savedCart ? savedCart : [];
  };

  const [cart, setCart] = useState(loadCartFromSessionStorage);
  
  // State for products, loading, and error
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // State for cart notification
  const [notification, setNotification] = useState({
    show: false,
    product: null
  });

  // Fetch products from the backend
  const fetchProducts = useCallback(async () => {
    try {
      setLoading(true);
      const response = await fetch(`${backendURL}/api/products/all`);
      const data = await response.json();
      
      if (!response.ok) {
        throw new Error(data.message || 'Failed to fetch products');
      }

      setProducts(data);
      setError(null);
    } catch (err) {
      setError(err.message);
      console.error('Error fetching products:', err);
      
      // Optional: Set dummy products if fetch fails
      const dummyProducts = [
        {
          _id: "1",
          name: "Cotton Summer Dress",
          price: 2499,
          category: "Tops and tunics",
          Collection: "Esther",
          material: "Cotton",
          images: ["/placeholder-image.jpg", "/placeholder-image-2.jpg"]
        },
        {
          _id: "2",
          name: "Silk Formal Shirt",
          price: 3599,
          category: "Bottomwear",
          Collection: "Rivaayat",
          material: "Silk",
          images: ["/placeholder-image.jpg", "/placeholder-image-2.jpg"]
        }
      ];
      
      setProducts(dummyProducts);
    } finally {
      setLoading(false);
    }
  }, [backendURL]);

  // Method to get all products (for Collection component)
  const getAllProducts = useCallback(async () => {
    if (products.length === 0) {
      await fetchProducts();
    }
    return products;
  }, [products, fetchProducts]);

  // Initial fetch on component mount
  useEffect(() => {
    fetchProducts();
  }, [fetchProducts]);

  // Add item to cart with specified quantity
  const addToCart = (product, size, quantity = 1) => {
    if (!size) {
      alert("Please select a size before adding to cart.");
      return;
    }
    
    // Ensure quantity is a valid number
    const qtyToAdd = (typeof quantity === 'number' && quantity > 0) ? quantity : 1;

    setCart((prevCart) => {
      const existingItem = prevCart.find(item => item.id === product._id && item.size === size);

      if (existingItem) {
        return prevCart.map(item =>
          item.id === product._id && item.size === size
            ? { ...item, quantity: item.quantity + qtyToAdd }
            : item
        );
      } else {
        // Make sure we use the right ID field
        return [...prevCart, { 
          ...product, 
          id: product._id || product.id, // Use _id if available, fallback to id 
          size, 
          quantity: qtyToAdd 
        }];
      }
    });
    
    // Show notification
    setNotification({
      show: true,
      product: product
    });
    
    // Hide notification after 2 seconds
    setTimeout(() => {
      setNotification({
        show: false,
        product: null
      });
    }, 2000);
  };

  // New function: Buy Now - clears cart and adds only the specified product
  const buyNow = (product, size, quantity = 1) => {
    if (!size) {
      alert("Please select a size before buying.");
      return false;
    }
    
    // Ensure quantity is a valid number
    const qtyToAdd = (typeof quantity === 'number' && quantity > 0) ? quantity : 1;
    
    // Create a new cart with just this product
    const newProduct = { 
      ...product, 
      id: product._id || product.id, // Use _id if available, fallback to id 
      size, 
      quantity: qtyToAdd 
    };
    
    // Set cart to contain only this product
    setCart([newProduct]);
    
    // Save to session storage
    sessionStorage.setItem('cart', JSON.stringify([newProduct]));
    
    return true;
  };

  // Remove item from cart
  const removeFromCart = (productId, size) => {
    setCart((prevCart) => {
      const updatedCart = prevCart.filter(item => !(item.id === productId && item.size === size));
      
      // If cart is empty, also clear the session storage
      if (updatedCart.length === 0) {
        sessionStorage.removeItem('cart');
      }
      
      return updatedCart;
    });
  };

  // Clear entire cart
  const clearCart = () => {
    setCart([]);
    sessionStorage.removeItem('cart');
  };

  // Update item quantity in the cart
  const updateQuantity = (productId, size, quantity) => {
    if (quantity <= 0) {
      removeFromCart(productId, size);
      return;
    }
    
    setCart((prevCart) =>
      prevCart.map((item) =>
        item.id === productId && item.size === size
          ? { ...item, quantity }
          : item
      )
    );
  };

  // Save cart to sessionStorage when cart changes
  useEffect(() => {
    if (cart.length > 0) {
      sessionStorage.setItem('cart', JSON.stringify(cart));
    } else {
      sessionStorage.removeItem('cart');
    }
  }, [cart]);

  // Cart notification component
  const CartNotification = () => {
    if (!notification.show || !notification.product) return null;
    
    // Get the image URL
    const imageUrl = notification.product.images && notification.product.images.length > 0
      ? (notification.product.images[0].secure_url || notification.product.images[0])
      : '/placeholder-image.jpg';
    
    return (
      <div className="cart-notification-container">
        <div className="cart-notification-content">
          <div className="cart-notification-image">
            <img src={imageUrl} alt={notification.product.name} />
          </div>
          <div className="cart-notification-info">
            <h4>Added to Cart</h4>
            <p>{notification.product.name}</p>
          </div>
        </div>
      </div>
    );
  };

  // Value to be provided to context consumers
  const value = {
    products,
    getAllProducts,
    currency,
    delivery_fee,
    cart,
    addToCart,
    buyNow,           // Add new buyNow function to context
    removeFromCart,
    updateQuantity,
    clearCart,
    loading,
    error,
    CartNotification
  };

  return (
    <ShopContext.Provider value={value}>
      {props.children}
      <CartNotification />
    </ShopContext.Provider>
  );
};

export default ShopContextProvider;